<template>
  <b-form>
    <h6 class="text-primary border-bottom pb-1 mb-2">
      <i class="fas fa-info-circle mr-1"></i> ข้อมูลชุดบัตร
    </h6>
    <b-form-group label="ชื่อแคมเปญ *" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-input v-model="form.name" required size="sm" />
    </b-form-group>
    <b-form-group
      label="คำนำหน้ารหัส *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="prefixError ? false : null"
      :invalid-feedback="prefixError"
    >
      <b-form-input
        v-model="form.codePrefix"
        :state="prefixError ? false : null"
        style="text-transform: uppercase"
        size="sm"
      />
    </b-form-group>
    <b-form-group
      label="จำนวนใบ *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="quantityError ? false : null"
      :invalid-feedback="quantityError"
    >
      <b-form-input
        v-model.number="form.quantity"
        type="number"
        min="1"
        :state="quantityError ? false : null"
        size="sm"
      />
    </b-form-group>
    <b-form-group label="ที่มา / ช่องทางการแจก" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-textarea v-model="form.channelNote" rows="2" size="sm" />
    </b-form-group>

    <h6 class="text-primary border-bottom pb-1 mb-2 mt-3">
      <i class="fas fa-calendar-alt mr-1"></i> ช่วงเวลาที่ใช้ได้
    </h6>
    <b-form-group label="วันเริ่มใช้" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-datepicker v-model="form.validFrom" reset-button placeholder="ไม่กำหนด" size="sm" />
    </b-form-group>
    <b-form-group
      label="วันหมดอายุ"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="dateError ? false : null"
      :invalid-feedback="dateError"
    >
      <b-form-datepicker v-model="form.validTo" reset-button placeholder="ไม่กำหนด" size="sm" />
    </b-form-group>

    <h6 class="text-primary border-bottom pb-1 mb-2 mt-3">
      <i class="fas fa-calculator mr-1"></i> มูลค่าส่วนลด
    </h6>
    <b-form-group label="ขอบเขต" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-radio-group v-model="form.scope" :options="scopeOptions" size="sm" />
    </b-form-group>
    <b-form-group label="ประเภทส่วนลด" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-radio-group v-model="form.discountType" :options="typeOptions" size="sm" />
      <!-- Both types are legal for both scopes, so the form never blocks the
           combination — it states the one limitation the campaign owner has to
           price in themselves before printing the cards. -->
      <small v-if="isUnboundedReceiptPercent" class="text-warning d-block mt-1">
        ส่วนลด % ทั้งใบเสร็จไม่มีเพดานเป็นจำนวนเงิน — บัตรใบเดียวกันอาจลด 300 บาท
        หรือ 30,000 บาท ขึ้นกับยอดบิล ถ้าต้องการจำกัดมูลค่าต่อใบ ให้ใช้บัตรแบบจำนวนเงินแทน
      </small>
    </b-form-group>
    <b-form-group
      label="มูลค่า *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="valueError ? false : null"
      :invalid-feedback="valueError"
    >
      <b-input-group size="sm" :append="form.discountType === 0 ? '%' : 'บาท'">
        <b-form-input
          v-model.number="form.discountValue"
          type="number"
          min="0"
          :state="valueError ? false : null"
          required
        />
      </b-input-group>
    </b-form-group>
    <b-form-group label="ผู้รับภาระ" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-radio-group v-model="form.discountMethod" :options="methodOptions" size="sm" />
    </b-form-group>
    <template v-if="isSpecificMethod">
      <b-form-group label="ส่วนที่หมอรับ" label-cols-sm="3" label-size="sm" class="mb-2">
        <b-input-group size="sm" :append="form.discountType === 0 ? '%' : 'บาท'">
          <b-form-input v-model.number="form.doctorDiscountValue" type="number" min="0" />
        </b-input-group>
      </b-form-group>
      <b-form-group label="ส่วนที่คลินิกรับ" label-cols-sm="3" label-size="sm" class="mb-2">
        <b-input-group size="sm" :append="form.discountType === 0 ? '%' : 'บาท'">
          <b-form-input v-model.number="form.clinicDiscountValue" type="number" min="0" />
        </b-input-group>
      </b-form-group>
    </template>
    <b-form-group
      :label="maxPercentLabel"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
    >
      <b-form-input v-model.number="form.maxDiscountPercent" type="number" min="0" max="100" placeholder="ไม่จำกัด" size="sm" />
      <small class="text-muted">เกินเพดาน = ใช้บัตรไม่ได้ (ระบบไม่ตัดยอดลงมาให้เท่าเพดาน)</small>
    </b-form-group>
    <b-form-group label="ยอดใบเสร็จขั้นต่ำ" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-input v-model.number="form.minReceiptAmount" type="number" min="0" placeholder="ไม่กำหนด" size="sm" />
    </b-form-group>

    <h6 class="text-primary border-bottom pb-1 mb-2 mt-3">
      <i class="fas fa-sliders-h mr-1"></i> เงื่อนไขการใช้
    </h6>
    <b-form-group label="จำกัดสาขา" label-cols-sm="3" label-size="sm" class="mb-2">
      <BranchPicker v-model="form.branchIds" />
      <small class="text-muted">ไม่เลือก = ใช้ได้ทุกสาขา</small>
    </b-form-group>
    <b-form-group label="เฉพาะแพทย์" label-cols-sm="3" label-size="sm" class="mb-2">
      <DoctorPicker v-model="form.doctorIds" :clinic-url="clinicUrl" :branch-url="branchUrl" />
    </b-form-group>
    <b-form-group label="เฉพาะกลุ่มผู้ป่วย" label-cols-sm="3" label-size="sm" class="mb-2">
      <PatientGroupPicker v-model="form.patientGroupIds" :clinic-url="clinicUrl" :branch-url="branchUrl" />
    </b-form-group>
    <b-form-group label="จำกัดรายการรักษา" label-cols-sm="3" label-size="sm" class="mb-2">
      <TreatmentPicker v-model="form.treatments" :clinic-url="clinicUrl" :branch-url="branchUrl" />
    </b-form-group>

    <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

    <div class="d-flex justify-content-end">
      <b-button variant="light" class="mr-2" @click="$emit('cancel')">ยกเลิก</b-button>
      <b-button variant="primary" :disabled="!canSubmit" @click="submit">สร้างชุดบัตร</b-button>
    </div>
    <small class="text-muted d-block text-right mt-1">
      ชุดบัตรจะถูกสร้างเป็นสถานะ "ร่าง" — ต้องกดเปิดใช้ก่อนรหัสจึงจะใช้ได้
    </small>
  </b-form>
</template>

<script>
import { mapActions } from "vuex";
import BranchPicker from "@/components/discount/BranchPicker.vue";
import DoctorPicker from "@/components/discount/DoctorPicker.vue";
import PatientGroupPicker from "@/components/discount/PatientGroupPicker.vue";
import TreatmentPicker from "@/components/discount/TreatmentPicker.vue";

const MAX_QUANTITY = 10000; // mirrors createBatch.js:6

export default {
  name: "VoucherBatchForm",
  components: { BranchPicker, DoctorPicker, PatientGroupPicker, TreatmentPicker },
  data() {
    return {
      isSaving: false,
      errorMessage: "",
      form: {
        name: "",
        codePrefix: "",
        quantity: 100,
        scope: "RECEIPT",
        channelNote: "",
        validFrom: null,
        validTo: null,
        discountType: 1,
        discountValue: 0,
        discountMethod: 0,
        doctorDiscountValue: null,
        clinicDiscountValue: null,
        maxDiscountPercent: null,
        branchIds: [],
        doctorIds: [],
        patientGroupIds: [],
        minReceiptAmount: null,
        treatments: [],
      },
      scopeOptions: [
        { text: "ทั้งใบเสร็จ", value: "RECEIPT" },
        { text: "รายหัตถการ", value: "ITEM" },
      ],
      typeOptions: [
        { text: "เปอร์เซ็นต์", value: 0 },
        { text: "บาท", value: 1 },
      ],
      methodOptions: [
        { text: "ตามสัดส่วน", value: 0 },
        { text: "ระบุเอง", value: 1 },
      ],
    };
  },
  computed: {
    clinicUrl() { return this.$route.params.clinicUrl; },
    branchUrl() { return this.$route.params.branchUrl; },
    isReceiptScope() { return this.form.scope === "RECEIPT"; },
    // A percentage off a whole bill is the one campaign shape whose card value
    // cannot be printed: it is worth whatever the receipt happens to total.
    // maxDiscountPercent cannot bound it either — that ceiling is a ratio of the
    // same total, so it either never trips or blocks the card outright — and the
    // data model has no max-baht field. A fixed-baht card is the only bounded
    // one, which is what the warning says instead of offering a fix that isn't.
    isUnboundedReceiptPercent() {
      return this.isReceiptScope && Number(this.form.discountType) === 0;
    },
    maxPercentLabel() {
      return this.isReceiptScope
        ? "เพดานส่วนลดต่อใบเสร็จ (%)"
        : "เพดานส่วนลดต่อบรรทัด (%)";
    },
    isSpecificMethod() { return Number(this.form.discountMethod) === 1; },
    // The input is uppercased by CSS (text-transform) and by buildPayload, so
    // validate what will actually be SENT, not the raw keystrokes. Testing the
    // raw model made "promo" — which reads PROMO on screen — fail with "ต้องเป็น
    // ตัวอักษรอังกฤษพิมพ์ใหญ่" and no visible way to satisfy it. Trim likewise:
    // buildPayload trims, so a trailing space must not block submit either.
    normalizedPrefix() {
      return (this.form.codePrefix || "").trim().toUpperCase();
    },
    prefixError() {
      if (!this.normalizedPrefix) return "";
      return /^[A-Z]{2,20}$/.test(this.normalizedPrefix)
        ? "" : "คำนำหน้าต้องเป็นตัวอักษรอังกฤษพิมพ์ใหญ่ 2–20 ตัว";
    },
    quantityError() {
      const q = Number(this.form.quantity);
      if (!Number.isInteger(q) || q < 1 || q > MAX_QUANTITY) {
        return `จำนวนใบต้องเป็นจำนวนเต็ม 1–${MAX_QUANTITY.toLocaleString()}`;
      }
      return "";
    },
    dateError() {
      if (!this.form.validFrom || !this.form.validTo) return "";
      return this.form.validFrom > this.form.validTo
        ? "วันเริ่มใช้ต้องไม่เกินวันหมดอายุ" : "";
    },
    valueError() {
      const value = Number(this.form.discountValue);
      if (!Number.isFinite(value) || value <= 0) return "มูลค่าส่วนลดต้องมากกว่า 0";
      // The server stores >100 happily but the calculation layer clamps it at
      // redemption, so a "150%" campaign would report 150 and behave as 100.
      // Refuse it here — this is the last screen that can still say so.
      if (Number(this.form.discountType) === 0 && value > 100) {
        return "ส่วนลดแบบเปอร์เซ็นต์ต้องไม่เกิน 100";
      }
      return "";
    },
    canSubmit() {
      // normalizedPrefix, not form.codePrefix: a whitespace-only entry is truthy
      // raw but empty once trimmed, and prefixError stays silent on empty — that
      // combination would enable submit and post an empty prefix.
      return !this.isSaving && !!this.form.name && !!this.normalizedPrefix
        && !this.prefixError && !this.quantityError && !this.dateError
        && !this.valueError;
    },
  },
  methods: {
    ...mapActions("moduleVoucher", ["createBatch"]),

    buildPayload() {
      const f = this.form;
      const data = {
        name: f.name.trim(),
        codePrefix: this.normalizedPrefix,
        quantity: Number(f.quantity),
        scope: f.scope,
        discountType: Number(f.discountType),
        discountValue: Number(f.discountValue),
        discountMethod: Number(f.discountMethod),
      };
      if (f.channelNote) data.channelNote = f.channelNote.trim();
      if (f.validFrom) data.validFrom = f.validFrom;
      if (f.validTo) data.validTo = f.validTo;
      if (this.isSpecificMethod) {
        data.doctorDiscountValue = Number(f.doctorDiscountValue || 0);
        data.clinicDiscountValue = Number(f.clinicDiscountValue || 0);
      }
      // Sent for both scopes: ITEM caps the discount against the line's price,
      // RECEIPT against the eligible lines' post-item-discount total.
      if (f.maxDiscountPercent != null && f.maxDiscountPercent !== "") {
        data.maxDiscountPercent = Number(f.maxDiscountPercent);
      }
      if (f.minReceiptAmount != null && f.minReceiptAmount !== "") {
        data.minReceiptAmount = Number(f.minReceiptAmount);
      }
      if (f.branchIds.length) data.branchIds = f.branchIds;
      if (f.doctorIds.length) data.doctorIds = f.doctorIds;
      if (f.patientGroupIds.length) data.patientGroupIds = f.patientGroupIds;
      if (f.treatments.length) data.treatments = f.treatments;
      return data;
    },

    async submit() {
      if (!this.canSubmit) return;
      this.isSaving = true;
      this.errorMessage = "";
      try {
        const result = await this.createBatch({
          clinicUrl: this.$route.params.clinicUrl,
          data: this.buildPayload(),
        });
        this.$emit("created", result);
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || "สร้างชุดบัตรไม่สำเร็จ";
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
